import { createChatRoom } from '@commenda/api';
import { ChatRoomType, TaskParty } from '@commenda/prisma';
import { ChatRoom } from '../../../chat/ChatRoom';

interface IndirectTaxFilingDetailServiceInboxProps {
  chatRoomId?: string;
  serviceRequestId?: string;
  userId?: string;
  companyId?: number;
  onChatChange?: () => void;
  /**
   * Who is viewing. CLIENT (default) hides the agent-only composer features and
   * routes the second tab to the client's own CUSTOMER_INTERNAL channel; AGENT
   * gets the full composer + internal-notes channel. Mirrors the role gating in
   * FilingReview / ServiceRequestLayoutBase. Defaults to CLIENT so a call site
   * that forgets the prop fails safe (hides agent UI) rather than leaking it.
   */
  role?: TaskParty;
}

const IndirectTaxFilingDetailServiceInbox = ({
  chatRoomId,
  serviceRequestId,
  userId,
  companyId,
  onChatChange,
  role = TaskParty.CLIENT,
}: IndirectTaxFilingDetailServiceInboxProps) => {
  const isClient = role === TaskParty.CLIENT;
  if (!userId || !serviceRequestId) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-md border-b border-border-primary p-xl">
          <span className="txt-small-bold text-text-primary">
            Service inbox
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="txt-small-regular text-text-tertiary">
            No service inbox available for this filing.
          </p>
        </div>
      </div>
    );
  }

  // Lazy-create the chat room on first open when no row exists yet. Matches the
  // V1 sales-tax filing flow (ChatRoomTrigger.onCreateOrThrowChatRoom) so old
  // filings without a pre-seeded ChatRoom still work.
  const onCreateOrThrowChatRoom = async (): Promise<string> => {
    const res = await createChatRoom({
      createChatRoomProps: {
        chatRoomType: ChatRoomType.SERVICE_REQUEST_THREAD,
        serviceRequestId,
      },
    });
    if (!res.ok) throw new Error('Failed to create chat room');
    const data = await res.json();
    const newChatRoomId: string | undefined = data?.chatRoom?.id;
    if (!newChatRoomId) throw new Error('Chat room id missing from response');
    onChatChange?.();
    return newChatRoomId;
  };

  return (
    <div className="flex h-full flex-col">
      <ChatRoom
        userId={userId}
        companyId={companyId}
        afterChange={onChatChange ? async () => onChatChange() : undefined}
        chatRoomInfo={{
          chatRoomId,
          chatRoomType: ChatRoomType.SERVICE_REQUEST_THREAD,
          serviceRequestId,
        }}
        onCreateOrThrowChatRoom={onCreateOrThrowChatRoom}
        isChatClosedOverride={false}
        subscriberViewerRole={isClient ? 'CUSTOMER' : 'AGENT'}
        allowCloseChatRoom={!isClient}
        allowReopenChatRoom={!isClient}
        hideFeatures={isClient}
        hideInternalNotes={isClient}
        includeCustomerInternalNotes={isClient}
      />
    </div>
  );
};

export { IndirectTaxFilingDetailServiceInbox };
